// BOORO: the log. What the agents and the people of a space did, with the
// costs of it. Only owners and admins of the space read it.
import { Trans, useLingui } from "@lingui/react/macro";
import type { AuditEntry, AuditKind } from "@rakazo/contracts";
import { Button, Label } from "@rakazo/ui-web";
import { Download } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { rpc } from "../../lib/rpc";
import { errorText } from "../../lib/user-error";
import type { WorkspaceChrome } from "./workspace-header";
import { WorkspaceViewHeader } from "./workspace-header";

type Bot = { id: string; name: string };

function useKindLabels(): Record<AuditKind, string> {
  const { t } = useLingui();
  return { task: t`Tasks`, run: t`Runs`, action: t`Actions`, decision: t`Decisions` };
}

function usePeriods(): { days: number; label: string }[] {
  const { t } = useLingui();
  return [
    { days: 7, label: t`7 days` },
    { days: 30, label: t`30 days` },
    { days: 90, label: t`90 days` },
  ];
}

/** Whether the person may read the log; false until the server said so. */
export function useCanReadLog(enabled: boolean): boolean {
  const [allowed, setAllowed] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    void rpc.booroAudit
      .allowed()
      .then((next) => {
        if (active) setAllowed(next.allowed);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [enabled]);
  return allowed;
}

/** The time of an entry, in the time zone of the browser. */
function formatTime(at: string): string {
  return new Date(at).toLocaleString();
}

export function LogView({ title, chrome }: { title: string; chrome: WorkspaceChrome }) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const kindLabels = useKindLabels();
  const periods = usePeriods();
  const [days, setDays] = useState(30);
  const [botId, setBotId] = useState("");
  const [kind, setKind] = useState<AuditKind | "">("");
  const [bots, setBots] = useState<Bot[]>([]);
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    void rpc.bots
      .list()
      .then((next) => {
        if (active) setBots(next as unknown as Bot[]);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  const filter = useCallback(
    () => ({
      days,
      ...(botId ? { botId } : {}),
      ...(kind ? { kinds: [kind] } : {}),
    }),
    [days, botId, kind],
  );

  const reload = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const page = await rpc.booroAudit.list(filter());
      setEntries(page.entries);
      setCursor(page.cursor);
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }, [filter]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function loadMore() {
    if (!cursor) return;
    setBusy(true);
    setError("");
    try {
      const page = await rpc.booroAudit.list({ ...filter(), cursor });
      setEntries((previous) => [...previous, ...page.entries]);
      setCursor(page.cursor);
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  async function exportCsv() {
    setBusy(true);
    setError("");
    try {
      const { csv } = await rpc.booroAudit.exportCsv(filter());
      // The byte order mark in the text makes Excel read it as UTF-8.
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `booro-protokoll-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <WorkspaceViewHeader title={title} chrome={chrome} />
      <div className="rk-scroll flex-1 overflow-y-auto px-4 pt-4 pb-24 md:px-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <Label className="text-[12px] text-muted-foreground" htmlFor="log-period">
              <Trans>Period</Trans>
            </Label>
            <select
              id="log-period"
              className="h-8 rounded-lg border border-border bg-background px-2 text-[13.5px] text-foreground"
              value={days}
              onChange={(event) => setDays(Number(event.target.value))}
            >
              {periods.map((period) => (
                <option key={period.days} value={period.days}>
                  {period.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-[12px] text-muted-foreground" htmlFor="log-bot">
              <Trans>Agent</Trans>
            </Label>
            <select
              id="log-bot"
              className="h-8 rounded-lg border border-border bg-background px-2 text-[13.5px] text-foreground"
              value={botId}
              onChange={(event) => setBotId(event.target.value)}
            >
              <option value="">{t`All agents`}</option>
              {bots.map((bot) => (
                <option key={bot.id} value={bot.id}>
                  {bot.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-[12px] text-muted-foreground" htmlFor="log-kind">
              <Trans>Kind</Trans>
            </Label>
            <select
              id="log-kind"
              className="h-8 rounded-lg border border-border bg-background px-2 text-[13.5px] text-foreground"
              value={kind}
              onChange={(event) => setKind(event.target.value as AuditKind | "")}
            >
              <option value="">{t`Everything`}</option>
              {(Object.keys(kindLabels) as AuditKind[]).map((entry) => (
                <option key={entry} value={entry}>
                  {kindLabels[entry]}
                </option>
              ))}
            </select>
          </div>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void exportCsv()}>
            <Download className="me-1.5" size={14} strokeWidth={1.8} />
            <Trans>Export CSV</Trans>
          </Button>
        </div>

        {error ? (
          <p role="alert" className="mt-4 text-sm text-destructive">
            {error}
          </p>
        ) : null}

        <div className="mt-4 overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="border-b border-border text-start text-[12px] tracking-wider uppercase text-muted-foreground">
                <th className="px-4 py-2.5 text-start font-medium">
                  <Trans>Time</Trans>
                </th>
                <th className="px-4 py-2.5 text-start font-medium">
                  <Trans>Who</Trans>
                </th>
                <th className="px-4 py-2.5 text-start font-medium">
                  <Trans>What</Trans>
                </th>
                <th className="px-4 py-2.5 text-start font-medium">
                  <Trans>Task</Trans>
                </th>
                <th className="px-4 py-2.5 text-start font-medium">
                  <Trans>Result</Trans>
                </th>
                <th className="px-4 py-2.5 text-end font-medium">
                  <Trans>Cost</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className="border-b border-border last:border-b-0">
                  <td className="whitespace-nowrap px-4 py-2.5 text-muted-foreground">
                    {formatTime(entry.at)}
                  </td>
                  <td className="px-4 py-2.5" dir="auto">
                    {entry.actor}
                  </td>
                  <td className="px-4 py-2.5" dir="auto">
                    {entry.action}
                  </td>
                  <td className="px-4 py-2.5" dir="auto">
                    {entry.taskId && entry.botId ? (
                      <button
                        type="button"
                        className="text-start underline-offset-2 hover:underline"
                        onClick={() =>
                          navigate(
                            `/app/${entry.botId}?task=${encodeURIComponent(entry.taskId ?? "")}`,
                          )
                        }
                      >
                        {entry.taskRef} {entry.taskTitle}
                      </button>
                    ) : entry.taskId ? (
                      `${entry.taskRef ?? ""} ${entry.taskTitle ?? ""}`.trim()
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-2.5" dir="auto">
                    {entry.outcome ?? "—"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-end">
                    {entry.costUsd === null ? "—" : `$${entry.costUsd.toFixed(4)}`}
                  </td>
                </tr>
              ))}
              {entries.length === 0 && !error ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                    <Trans>Nothing happened in this period.</Trans>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {cursor ? (
          <div className="mt-3 flex justify-center">
            <Button size="sm" variant="outline" disabled={busy} onClick={() => void loadMore()}>
              <Trans>Load more</Trans>
            </Button>
          </div>
        ) : null}
      </div>
    </>
  );
}
